import { isWeb } from "@memorang/helpers/platform";
import type { TextStyle } from "react-native";
import { Drawer, Icon, Text, TouchableRipple } from "react-native-paper";
import { useAppTheme } from "../../../useAppTheme";
import Box from "../../Box";
import type { NavItem } from "../types";

type Props = {
	collapsed: boolean;
	active?: boolean;
	label: string;
	subtitle?: string;
	icon?: NavItem["icon"];
	activeIcon?: NavItem["activeIcon"];
	onPress: () => void;
};

type VariantProps = Omit<Props, "collapsed" | "subtitle" | "activeIcon"> & {
	icon?: NavItem["icon"];
	subtitle?: string;
};

// Replicate Drawer.CollapsedItem to support padding and word break
const CollapsedWrappingItem = ({
	active,
	label,
	icon,
	onPress,
}: VariantProps) => {
	const theme = useAppTheme();
	return (
		<TouchableRipple
			borderless
			onPress={onPress}
			accessibilityRole="button"
			accessibilityState={{ selected: active }}
		>
			<Box
				width={80}
				alignItems="center"
				paddingHorizontal={12}
				paddingBottom={12}
			>
				<Box
					width={56}
					height={32}
					borderRadius={16}
					alignItems="center"
					justifyContent="center"
					backgroundColor={
						active ? theme.colors.secondaryContainer : "transparent"
					}
				>
					{icon ? (
						<Icon
							source={icon}
							size={24}
							color={
								active
									? theme.colors.onSecondaryContainer
									: theme.colors.onSurfaceVariant
							}
						/>
					) : null}
				</Box>
				<Text
					variant="labelMedium"
					numberOfLines={2}
					style={[
						{
							marginTop: 4,
							textAlign: "center",
							color: active
								? theme.colors.onSurface
								: theme.colors.onSurfaceVariant,
						},
						isWeb
							? ({ wordBreak: "break-word" } as unknown as TextStyle)
							: null,
					]}
				>
					{label}
				</Text>
			</Box>
		</TouchableRipple>
	);
};

// Replicate Drawer.Item to support multiple lines
const MultilineDrawerItem = ({
	active,
	label,
	subtitle,
	icon,
	onPress,
}: VariantProps) => {
	const theme = useAppTheme();
	const contentColor = active
		? theme.colors.onSecondaryContainer
		: theme.colors.onSurfaceVariant;
	return (
		<TouchableRipple
			borderless
			onPress={onPress}
			accessibilityRole="button"
			accessibilityState={{ selected: active }}
			style={{
				borderRadius: 7 * theme.roundness,
				backgroundColor: active ? theme.colors.secondaryContainer : undefined,
			}}
		>
			<Box
				flexDirection="row"
				alignItems="center"
				minHeight={56}
				paddingVertical={8}
				paddingLeft={16}
				paddingRight={24}
				gap={12}
			>
				{icon ? <Icon source={icon} size={24} color={contentColor} /> : null}
				<Box flex={1}>
					<Text
						variant="labelLarge"
						numberOfLines={1}
						style={{ color: contentColor }}
					>
						{label}
					</Text>
					<Text
						variant="bodySmall"
						numberOfLines={1}
						style={{ color: contentColor }}
					>
						{subtitle}
					</Text>
				</Box>
			</Box>
		</TouchableRipple>
	);
};

export const NavRailItem = ({
	collapsed,
	active,
	label,
	subtitle,
	icon,
	activeIcon,
	onPress,
}: Props) => {
	const currentIcon = active ? (activeIcon ?? icon) : icon;

	if (collapsed) {
		if (!subtitle) {
			return (
				<Drawer.CollapsedItem
					active={active}
					onPress={onPress}
					label={label}
					focusedIcon={activeIcon}
					unfocusedIcon={icon}
					style={{ marginHorizontal: 0 }}
				/>
			);
		}
		return (
			<CollapsedWrappingItem
				active={active}
				label={label}
				icon={currentIcon}
				onPress={onPress}
			/>
		);
	}

	if (!subtitle) {
		return (
			<Drawer.Item
				active={active}
				onPress={onPress}
				label={label}
				icon={currentIcon}
				style={{ marginHorizontal: 0 }}
			/>
		);
	}

	return (
		<MultilineDrawerItem
			active={active}
			label={label}
			subtitle={subtitle}
			icon={currentIcon}
			onPress={onPress}
		/>
	);
};
